import React, { useState, useRef } from 'react';
import type { CampaignSubmission, MediaPart } from '../types';
import { generateCampaignReview } from '../services/geminiService';
import { ClipboardCheckIcon, UploadIcon, FileIcon, ImageIcon, VideoIcon, MusicIcon, PdfIcon, XCircleIcon } from '../components/icons';

declare global {
  interface Window {
    marked: {
      parse(markdown: string): string;
    };
  }
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;
const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/mpeg", "video/webm", "audio/mpeg", "audio/wav", "audio/ogg"];


const fileToBase64 = (file: File): Promise<MediaPart> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            const data = result.split(',')[1];
            resolve({ inlineData: { data, mimeType: file.type } });
        };
        reader.onerror = error => reject(error);
    });
};

const FileTypeIcon: React.FC<{ mimeType: string }> = ({ mimeType }) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-text-secondary" />;
    if (mimeType.startsWith('video/')) return <VideoIcon className="w-5 h-5 text-text-secondary" />;
    if (mimeType.startsWith('audio/')) return <MusicIcon className="w-5 h-5 text-text-secondary" />;
    return <FileIcon className="w-5 h-5 text-text-secondary" />;
};


const CampaignReviewView: React.FC = () => {
    const [campaigns, setCampaigns] = useState<CampaignSubmission[]>([]);
    // Form state
    const [clientName, setClientName] = useState('');
    const [campaignName, setCampaignName] = useState('');
    const [targetAudience, setTargetAudience] = useState('');
    const [budget, setBudget] = useState('');
    const [creativeAssets, setCreativeAssets] = useState<{ name: string; part: MediaPart }[]>([]);
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const resetForm = () => {
        setClientName('');
        setCampaignName('');
        setTargetAudience('');
        setBudget('');
        setCreativeAssets([]);
    };
    
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setError(null);
        const files = event.target.files;
        if (!files) return;

        if (creativeAssets.length + files.length > MAX_FILES) {
            setError(`You can upload a maximum of ${MAX_FILES} files.`);
            return;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
                continue;
            }
            if (!SUPPORTED_MIME_TYPES.includes(file.type)) {
                setError(`File type for "${file.name}" is not supported. Supported types are images, video, and audio.`);
                continue;
            }

            try {
                const mediaPart = await fileToBase64(file);
                setCreativeAssets(prev => [...prev, { name: file.name, part: mediaPart }]);
            } catch (err) {
                setError(`Failed to process file "${file.name}".`);
            }
        }
      };

    const removeFile = (fileName: string) => {
        setCreativeAssets(prev => prev.filter(f => f.name !== fileName));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!clientName || !campaignName || !targetAudience || !budget) {
            setError('Please fill out all required fields.');
            return;
        }

        setIsLoading(true);
        setError(null);

        const newCampaign: CampaignSubmission = {
            id: `campaign_${Date.now()}`,
            clientName,
            campaignName,
            targetAudience,
            budget: parseFloat(budget),
            creativeAssets,
            status: 'Analyzing',
        };
        
        setCampaigns(prev => [newCampaign, ...prev]);
        resetForm();

        try {
            const campaignData = { clientName, campaignName, targetAudience, budget: parseFloat(budget) };
            const assetParts = creativeAssets.map(asset => asset.part);
            const result = await generateCampaignReview(campaignData, assetParts);

            setCampaigns(prev => prev.map(c => 
                c.id === newCampaign.id 
                ? { ...c, status: result.status, reviewNotes: result.rationale } 
                : c
            ));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during review.";
            setCampaigns(prev => prev.map(c => 
                c.id === newCampaign.id 
                ? { ...c, status: 'Failed', reviewNotes: errorMessage } 
                : c
            ));
        } finally {
            setIsLoading(false);
        }
    };

    const StatusBadge: React.FC<{ status: CampaignSubmission['status']}> = ({ status }) => {
        const baseClasses = "text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full";
        switch (status) {
            case 'Approved':
                return <span className={`${baseClasses} bg-green-900 text-green-300`}>{status}</span>;
            case 'Changes Requested':
                return <span className={`${baseClasses} bg-yellow-900 text-yellow-300`}>{status}</span>;
            case 'Analyzing':
                return <span className={`${baseClasses} bg-blue-900 text-blue-300 animate-pulse`}>{status}</span>;
             case 'Failed':
                return <span className={`${baseClasses} bg-red-900 text-red-300`}>{status}</span>;
            default:
                return <span className={`${baseClasses} bg-gray-700 text-gray-300`}>{status}</span>;
        }
    };

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto h-full grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Column */}
            <div className="lg:pr-8">
                <div className="text-left mb-6">
                    <h2 className="text-2xl font-bold">Opus Campaign Review</h2>
                    <p className="text-text-secondary mt-2">Submit your campaign proposal for a comprehensive AI-powered analysis and approval.</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="clientName" className="block text-sm font-medium text-text-secondary">Client Name</label>
                        <input type="text" id="clientName" value={clientName} onChange={e => setClientName(e.target.value)} className="mt-1 block w-full bg-base-200 rounded-md p-2 border border-base-300 focus:border-brand-primary focus:outline-none" required />
                    </div>
                     <div>
                        <label htmlFor="campaignName" className="block text-sm font-medium text-text-secondary">Campaign Name</label>
                        <input type="text" id="campaignName" value={campaignName} onChange={e => setCampaignName(e.target.value)} className="mt-1 block w-full bg-base-200 rounded-md p-2 border border-base-300 focus:border-brand-primary focus:outline-none" required />
                    </div>
                     <div>
                        <label htmlFor="targetAudience" className="block text-sm font-medium text-text-secondary">Target Audience</label>
                        <textarea id="targetAudience" value={targetAudience} onChange={e => setTargetAudience(e.target.value)} rows={3} className="mt-1 block w-full bg-base-200 rounded-md p-2 border border-base-300 focus:border-brand-primary focus:outline-none resize-y" required />
                    </div>
                    <div>
                        <label htmlFor="budget" className="block text-sm font-medium text-text-secondary">Budget ($)</label>
                        <input type="number" id="budget" value={budget} onChange={e => setBudget(e.target.value)} min="0" step="100" className="mt-1 block w-full bg-base-200 rounded-md p-2 border border-base-300 focus:border-brand-primary focus:outline-none" required />
                    </div>
                     <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary">Creative Assets (Optional)</label>
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 text-sm font-medium bg-base-200 hover:bg-base-300 border border-dashed border-base-300 px-3 py-4 rounded-md transition-colors">
                            <UploadIcon className="w-5 h-5"/>
                            Add Files (Image, Video, Audio)
                        </button>
                         <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple accept={SUPPORTED_MIME_TYPES.join(',')} className="hidden" />
                        {creativeAssets.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                                {creativeAssets.map(file => (
                                    <div key={file.name} className="flex items-center justify-between bg-base-200 p-2 rounded-md text-sm">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <FileTypeIcon mimeType={file.part.inlineData.mimeType} />
                                            <span className="truncate text-text-secondary">{file.name}</span>
                                        </div>
                                        <button type="button" onClick={() => removeFile(file.name)} className="text-text-secondary hover:text-red-400">
                                            <XCircleIcon className="w-5 h-5"/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    
                    <button type="submit" disabled={isLoading} className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 disabled:bg-base-300">
                        {isLoading ? 'Analyzing...' : 'Submit for AI Review'}
                        {!isLoading && <ClipboardCheckIcon className="w-5 h-5" />}
                    </button>
                </form>
            </div>
            {/* Results Column */}
            <div className="lg:border-l lg:pl-8 border-base-300 flex flex-col h-full">
                <h3 className="text-xl font-bold mb-4 flex-shrink-0">Review History</h3>
                <div className="flex-1 overflow-y-auto pr-2 -mr-2">
                {campaigns.length === 0 && !isLoading && (
                    <div className="text-center py-10 border-2 border-dashed border-base-300 rounded-lg">
                        <p className="text-text-secondary">Submitted campaigns will appear here.</p>
                    </div>
                )}
                 {campaigns.map(campaign => (
                    <div key={campaign.id} className="bg-base-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-text-primary">{campaign.campaignName}</h4>
                            <StatusBadge status={campaign.status} />
                        </div>
                        <p className="text-sm text-text-secondary mb-3">Client: {campaign.clientName} | Budget: ${campaign.budget.toLocaleString()}</p>
                        {campaign.reviewNotes && (
                            <div className="prose prose-sm prose-invert max-w-none p-3 bg-base-300/50 rounded-md">
                                <div dangerouslySetInnerHTML={{ __html: window.marked.parse(campaign.reviewNotes) }} />
                            </div>
                        )}
                        {campaign.status === 'Analyzing' && (
                             <div className="flex items-center gap-2 p-3 bg-base-300/50 rounded-md text-sm text-text-secondary">
                                <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                <span className="ml-2">Opus is reviewing the proposal...</span>
                            </div>
                        )}
                    </div>
                ))}
                </div>
            </div>
        </div>
    );
};

export default CampaignReviewView;
